import type { Characteristic, Component } from '../schema';
import type { CharacteristicSelection, SelectionsMap } from '../state/selections';
import type { VisibilityRules } from '../state/effectiveSelections';
import type { PredefinedSelection } from './types';

/** One Characteristic to set to one Value. */
export interface PredefineWrite {
    characteristicId: number;
    valueId: number;
}

/**
 * Turn the predefine candidates of one rules pass into the writes they
 * actually mean — the single place that knows what a `predefine_values` mode
 * does.
 *
 * The modes come from the admin's own per-Characteristic select
 * (`RuleSettingsPanel.jsx`, PREDEFINE_CONDITIONS) and legacy implements them
 * in `rules/rule.js:344-420`, where `condition` is overwritten with `1` once a
 * mode qualifies and the apply path below reads it:
 *
 * - **`'1'` apply always** — legacy's `case "1": condition = 1`. Writes over
 *   whatever is there, which is the whole point of the mode.
 * - **`'2'` only on default value** — legacy's
 *   `if (!predefinedOption && currentValue === defaultValue) condition = 1`,
 *   plus its fall-through soft write `if (!option.getValue()) setValue(…)`
 *   for the case where nothing is picked at all. Both together are "no
 *   selection, or the selection is still the default".
 * - **`'3'` no selection or default value** — mode 2 plus
 *   `currentValue == '' || activeChoice.getIsBlacklisted()`. The blacklist
 *   half is the reported case: a rule removed the Value the customer had
 *   picked, and this fills the gap it left.
 *
 * Deliberately a value comparison rather than a "did the user touch this"
 * flag. That is what legacy compares, and it keeps the modes meaningful for
 * selections that never went through a click — deep link, cart restore, host
 * API.
 *
 * Reads the **raw** selections, not `effectiveSelections`: mode `'3'` has to
 * see that the current pick is blacklisted, and the effective map has already
 * dropped it by then.
 *
 * Pure — no store, no React. `ConfiguratorRoot` feeds the result to
 * `applyPredefine(…, { force: true })`, since the decision has been made here.
 */
export function resolvePredefineWrites(args: {
    component: Component;
    predefinedSelections: Readonly<Record<number, PredefinedSelection>>;
    selections: SelectionsMap;
    rules: VisibilityRules;
}): PredefineWrite[] {
    const { component, predefinedSelections, selections, rules } = args;
    const entries = Object.entries(predefinedSelections);
    if (entries.length === 0) return [];

    const byId = new Map<number, Characteristic>();
    for (const group of component.groups) {
        for (const char of group.characteristics) byId.set(char.id, char);
    }

    const writes: PredefineWrite[] = [];
    for (const [charIdStr, predef] of entries) {
        const characteristicId = Number(charIdStr);
        if (!Number.isFinite(characteristicId)) continue;
        const char = byId.get(characteristicId);
        // Rules outliving the Characteristic they point at is normal in long-
        // lived components (8 such entries across the local set).
        if (!char) continue;

        if (predef.dynamicValue) {
            // Legacy resolves the target from another Characteristic's current
            // value (rule.js:433-452). Unimplemented — skipped out loud rather
            // than silently writing the alt_title as if it were a Value ID.
            warnOnce(
                `dynamic.${characteristicId}`,
                `predefine rule for characteristic ${characteristicId} uses dynamicValue, which V3 does not support yet — skipped`,
            );
            continue;
        }
        const { valueId } = predef;
        if (valueId === undefined) continue;

        // ── The two locks legacy applies in every mode ──────────────────
        // Writing into a Characteristic another rule just removed would put a
        // price and a summary line behind something the page no longer shows.
        if (rules.hiddenCharacteristics.has(characteristicId)) continue;
        // Setting exactly the Value a blacklist rule forbids — legacy drops
        // the choice on `getIsBlacklisted()` (rule.js:405-408).
        if (rules.blacklistedValues.has(valueId)) continue;
        // A target that doesn't exist on a selection Characteristic reads as
        // "nothing picked" in the UI, so writing it would *create* the empty
        // required field this whole path exists to fill. Characteristics with
        // no Value list (text, expression) keep the legacy behaviour of
        // taking the raw number — `resolveLeaf` falls back to it.
        if ('values' in char && char.values.length > 0
            && !char.values.some((v) => v.id === valueId)) continue;

        if (qualifies(predef, char, selections[characteristicId], rules)) {
            writes.push({ characteristicId, valueId });
        }
    }
    return writes;
}

function qualifies(
    predef: PredefinedSelection,
    char: Characteristic,
    selection: CharacteristicSelection | undefined,
    rules: VisibilityRules,
): boolean {
    if (predef.condition === '1') return true;

    const current = readCurrentPick(selection);
    if (!current.hasSelection) return true;

    const defaultValueId = 'defaultValueId' in char ? char.defaultValueId : undefined;
    if (
        current.valueId !== undefined &&
        defaultValueId !== undefined &&
        current.valueId === defaultValueId
    ) {
        return true;
    }

    return predef.condition === '3'
        && current.valueId !== undefined
        && rules.blacklistedValues.has(current.valueId);
}

/**
 * What the customer currently has in a Characteristic, reduced to the two
 * things the modes ask about: is anything picked, and — for single picks — of
 * which Value. A one-element multi-pick collapses to that Value, the way
 * legacy unwraps a single-entry array (`rule.js:346-348`); a wider multi-pick
 * counts as "something is picked" without a comparable Value.
 */
function readCurrentPick(
    selection: CharacteristicSelection | undefined,
): { hasSelection: boolean; valueId: number | undefined } {
    const none = { hasSelection: false, valueId: undefined };
    if (!selection) return none;

    switch (selection.kind) {
        case 'single':
            return selection.valueId === null
                ? none
                : { hasSelection: true, valueId: selection.valueId };
        case 'multi':
            if (selection.valueIds.length === 0) return none;
            return {
                hasSelection: true,
                valueId: selection.valueIds.length === 1 ? selection.valueIds[0] : undefined,
            };
        case 'text':
            return selection.value.trim() === ''
                ? none
                : { hasSelection: true, valueId: undefined };
        case 'quantities':
            return Object.keys(selection.quantities).length === 0
                ? none
                : { hasSelection: true, valueId: undefined };
        case 'files':
            return selection.files.length === 0
                ? none
                : { hasSelection: true, valueId: undefined };
        case 'captcha':
            return selection.payload === ''
                ? none
                : { hasSelection: true, valueId: undefined };
    }
}

const seenWarnings = new Set<string>();

function warnOnce(key: string, message: string): void {
    if (seenWarnings.has(key)) return;
    seenWarnings.add(key);
    console.warn(`[Configento] ${message}`);
}
